import { WorkyObjectMetaData } from '../../../common/CommonInterfaces';
import { isValidString } from '../../../common/CommonUtilsMethods';

enum WorkerOnboardingChecklistTemplateStatus {
  DRAFT = 'DRAFT',
  ACTIVATED = 'ACTIVATED',
  ARCHIVED = 'ARCHIVED',
}

enum WorkerOnboardingChecklistTemplateType {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
}

enum WorkerOnboardingChecklistStepStatus {
  TODO = 'TODO',
  DONE = 'DONE',
  WAIVED = 'WAIVED',
}

enum WorkerOnboardingChecklistInstanceStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

interface WorkerOnboardingChecklistStepDefRequestDTO {
  readonly stepId?: string | null;
  readonly title: string;
  readonly description?: string | null;
  readonly sortOrder: number;
  readonly required: boolean;
}

interface WorkerOnboardingChecklistStepDefResponseDTO {
  readonly stepId: string;
  readonly title: string;
  readonly description?: string | null;
  readonly sortOrder: number;
  readonly required: boolean;
}

interface CreateWorkerOnboardingChecklistTemplateRequestDTO {
  readonly checklistName: string;
  readonly templateStatus: WorkerOnboardingChecklistTemplateStatus;
  readonly checklistTemplateType: WorkerOnboardingChecklistTemplateType;
  readonly steps: readonly WorkerOnboardingChecklistStepDefRequestDTO[];
}

interface UpdateWorkerOnboardingChecklistTemplateRequestDTO {
  readonly checklistTemplateId: string;
  readonly checklistName: string;
  readonly templateStatus: WorkerOnboardingChecklistTemplateStatus;
  readonly checklistTemplateType: WorkerOnboardingChecklistTemplateType;
  readonly steps: readonly WorkerOnboardingChecklistStepDefRequestDTO[];
}

interface WorkerOnboardingChecklistTemplateResponseDTO {
  readonly checklistTemplateId: string;
  readonly agencyName: string;
  readonly checklistName: string;
  readonly templateStatus: WorkerOnboardingChecklistTemplateStatus | string;
  readonly checklistTemplateType: WorkerOnboardingChecklistTemplateType | string;
  readonly templateVersion: number;
  readonly steps: readonly WorkerOnboardingChecklistStepDefResponseDTO[];
  readonly metaData: WorkyObjectMetaData;
}

interface AssignWorkerOnboardingChecklistInstanceRequestDTO {
  readonly primaryWorkyUserId: string;
  readonly checklistTemplateId: string;
}

interface UpdateWorkerOnboardingChecklistStepStatusRequestDTO {
  readonly checklistInstanceId: string;
  readonly stepId: string;
  readonly status: WorkerOnboardingChecklistStepStatus;
  readonly note?: string | null;
}

interface WorkerOnboardingChecklistStepStateResponseDTO {
  readonly stepId: string;
  readonly title: string;
  readonly description?: string | null;
  readonly sortOrder: number;
  readonly required: boolean;
  readonly status: WorkerOnboardingChecklistStepStatus | string;
  readonly updatedByPrimaryWorkyUserId?: string | null;
  readonly updatedAtEpochSeconds?: number | null;
  readonly note?: string | null;
}

interface WorkerOnboardingChecklistInstanceResponseDTO {
  readonly checklistInstanceId: string;
  readonly checklistTemplateId: string;
  readonly templateVersion: number;
  readonly agencyName: string;
  readonly primaryWorkyUserId: string;
  readonly checklistName: string;
  readonly checklistTemplateType: WorkerOnboardingChecklistTemplateType | string;
  readonly instanceStatus?: WorkerOnboardingChecklistInstanceStatus | string | null;
  readonly steps: readonly WorkerOnboardingChecklistStepStateResponseDTO[];
  readonly ready: boolean;
  readonly metaData: WorkyObjectMetaData;
}

interface WorkerOnboardingChecklistInstancesByWorkerResponseDTO {
  readonly primaryWorkyUserId: string;
  readonly instances: readonly WorkerOnboardingChecklistInstanceResponseDTO[];
  readonly aggregateReady: boolean;
}

interface JobberWorkerOnboardingChecklistSectionProps {
  readonly primaryWorkyUserId: string;
}

const getChecklistTemplateStatusLabel = (
  status: WorkerOnboardingChecklistTemplateStatus | string
): string => {
  if (status === WorkerOnboardingChecklistTemplateStatus.ACTIVATED || status === 'ACTIVATED') {
    return 'Activated';
  }
  if (status === WorkerOnboardingChecklistTemplateStatus.ARCHIVED || status === 'ARCHIVED') {
    return 'Archived';
  }
  return 'Draft';
};

const getChecklistTemplateTypeLabel = (
  type: WorkerOnboardingChecklistTemplateType | string
): string => {
  if (type === WorkerOnboardingChecklistTemplateType.PRIMARY || type === 'PRIMARY') {
    return 'Primary';
  }
  return 'Secondary';
};

const getChecklistStepStatusLabel = (
  status: WorkerOnboardingChecklistStepStatus | string
): string => {
  if (status === WorkerOnboardingChecklistStepStatus.DONE || status === 'DONE') {
    return 'Done';
  }
  if (status === WorkerOnboardingChecklistStepStatus.WAIVED || status === 'WAIVED') {
    return 'Waived';
  }
  return 'Pending';
};

const isChecklistTemplateAssignable = (
  template: WorkerOnboardingChecklistTemplateResponseDTO
): boolean => {
  return (
    template.templateStatus === WorkerOnboardingChecklistTemplateStatus.ACTIVATED
    || template.templateStatus === 'ACTIVATED'
  );
};

const sortChecklistStepsBySortOrder = <T extends { readonly sortOrder: number }>(
  steps: readonly T[]
): readonly T[] => {
  return [...steps].sort((left: T, right: T): number => left.sortOrder - right.sortOrder);
};

const buildEmptyChecklistStepDraft = (
  sortOrder: number
): WorkerOnboardingChecklistStepDefRequestDTO => {
  return {
    title: '',
    description: null,
    sortOrder,
    required: true,
  };
};

const hasValidChecklistStepTitle = (
  step: WorkerOnboardingChecklistStepDefRequestDTO
): boolean => {
  return isValidString(step.title);
};

export {
  WorkerOnboardingChecklistTemplateStatus,
  WorkerOnboardingChecklistTemplateType,
  WorkerOnboardingChecklistStepStatus,
  WorkerOnboardingChecklistInstanceStatus,
  WorkerOnboardingChecklistStepDefRequestDTO,
  WorkerOnboardingChecklistStepDefResponseDTO,
  CreateWorkerOnboardingChecklistTemplateRequestDTO,
  UpdateWorkerOnboardingChecklistTemplateRequestDTO,
  WorkerOnboardingChecklistTemplateResponseDTO,
  AssignWorkerOnboardingChecklistInstanceRequestDTO,
  UpdateWorkerOnboardingChecklistStepStatusRequestDTO,
  WorkerOnboardingChecklistStepStateResponseDTO,
  WorkerOnboardingChecklistInstanceResponseDTO,
  WorkerOnboardingChecklistInstancesByWorkerResponseDTO,
  JobberWorkerOnboardingChecklistSectionProps,
  getChecklistTemplateStatusLabel,
  getChecklistTemplateTypeLabel,
  getChecklistStepStatusLabel,
  isChecklistTemplateAssignable,
  sortChecklistStepsBySortOrder,
  buildEmptyChecklistStepDraft,
  hasValidChecklistStepTitle,
};
